import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import serviceTemplateService from '../../services/serviceTemplateService';
import { ServiceItem } from '../../types/service';
import { useAppSelector } from '../../hooks/useRedux';
import '../../styles/ServiceTemplatePage.css';

// Define the category type
type Category = {
  id: string;
  name: string;
};

// List of available categories
const CATEGORIES: Category[] = [
  { id: 'hair', name: 'Hair' },
  { id: 'braiding', name: 'Braiding' },
  { id: 'natural', name: 'Natural Hair' },
  { id: 'barber', name: 'Barber' },
  { id: 'extensions', name: 'Extensions' },
  { id: 'nails', name: 'Nails' },
  { id: 'makeup', name: 'Makeup' },
  { id: 'skincare', name: 'Skincare' },
  { id: 'lashes', name: 'Lashes' },
  { id: 'brows', name: 'Brows' },
  { id: 'waxing', name: 'Waxing' },
  { id: 'massage', name: 'Massage' },
  { id: 'wellness', name: 'Wellness' },
  { id: 'fitness', name: 'Fitness' },
  { id: 'therapy', name: 'Therapy' }
];

const ServiceTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAppSelector(state => state.auth);
  
  // Filter categories based on stylist's specialties
  const stylistSpecialties = auth.user?.stylistProfile?.specialties || [];
  const filteredCategories = stylistSpecialties.length > 0
    ? CATEGORIES.filter(cat => stylistSpecialties.includes(cat.id))
    : CATEGORIES; // Show all if no specialties (fallback)
  
  // State for selected category, templates data, loading and error messages
  const [selectedCategory, setSelectedCategory] = useState<string>(
    filteredCategories.length > 0 ? filteredCategories[0].id : CATEGORIES[0].id
  );
  const [templates, setTemplates] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  // Check if the user is logged in and is a stylist
  useEffect(() => {
    if (!auth.token) {
      navigate('/login');
    } else if (auth.user && auth.user.userType !== 'stylist') {
      navigate('/dashboard');
    }
  }, [auth, navigate]);

  // Fetch templates from the API when the selected category changes
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching templates for category:', selectedCategory);
      const templatesFromApi = await serviceTemplateService.getTemplatesByCategory(selectedCategory);
      console.log('Templates received:', templatesFromApi);
      
      // If API returns no templates, show informative message instead of empty array
      if (!templatesFromApi || templatesFromApi.length === 0) {
        console.log('No templates found for category:', selectedCategory);
        setTemplates([]);
        // Special message for development
        if (process.env.NODE_ENV === 'development') {
          setError('No templates available. Check API connection or use mock data.');
        }
      } else {
        setTemplates(templatesFromApi);
      }
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'Error fetching templates.');
      setTemplates([]);
      
      // For development, add fallback to mock data if needed
      if (process.env.NODE_ENV === 'development') {
        // Future implementation to add mock templates
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]); // fetchTemplates is stable

  // Handle category selection change
  const handleCategorySelect = (id: string) => {
    setSelectedCategory(id);
  };

  // When a template is used, navigate to the edit template page, passing the template in state
  const handleUseTemplate = (template: ServiceItem) => {
    console.log('Selected template:', template);
    navigate('/stylist/edit-template', { state: { template } });
  };

  // Show message if no templates available for selected specialties
  if (filteredCategories.length === 0 && stylistSpecialties.length > 0) {
    return (
      <div className="service-template-page">
        <div className="tesla-header">
          <h1 className="tesla-title">Service Templates</h1>
          <p className="tesla-subtitle">No templates available for your specialties</p>
        </div>
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
            </svg>
          </div>
          <h3>No Templates Available</h3>
          <p>No service templates match your selected specialties. Please update your profile settings.</p>
          <button 
            className="tesla-button-primary" 
            onClick={() => navigate('/stylist/profile')}
          >
            Update Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="service-template-page">
      {/* Header Section - Tesla-inspired */}
      <div className="tesla-header">
        <h1 className="tesla-title">Service Templates</h1>
        <p className="tesla-subtitle">Select a template to create a new service</p>
      </div>

      {/* Category Navigation - Horizontal Scrollable */}
      <div className="category-nav-container">
        <div className="category-nav">
          {filteredCategories.map((category) => (
            <button
              key={category.id}
              className={`category-button ${
                selectedCategory === category.id ? 'category-button-active' : ''
              }`}
              onClick={() => handleCategorySelect(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
      {/* Templates Grid */}
      <div className="templates-grid">
        {loading ? (
          <div className="tesla-loader">
            <div className="tesla-loader-spinner"></div>
            <p>Loading templates...</p>
          </div>
        ) : error ? (
          <div className="tesla-error">
            <p>{error}</p>
            <button className="tesla-button-secondary" onClick={fetchTemplates}>
              Try Again
            </button>
          </div>
        ) : templates.length > 0 ? (
          templates.map((template) => (
            <div key={template._id} className="template-card">
              <div className="template-card-content">
                <h2 className="template-name">{template.name}</h2>
                
                <div className="template-price-tag">
                  <span className="template-price-label">Price Range</span>
                  <span className="template-price-value">
                    {formatCurrency(template.basePrice.min)} - {formatCurrency(template.basePrice.max)}
                  </span>
                </div>
                
                <div className="template-description">
                  {typeof template.description === 'string'
                    ? template.description
                    : template.description.short || ''}
                </div>
                
                <div className="template-specs">
                  <div className="template-spec">
                    <span className="template-spec-label">Duration</span>
                    <span className="template-spec-value">{template.baseDuration.min} - {template.baseDuration.max} mins</span>
                  </div>
                  
                  {template.skillLevel && (
                    <div className="template-spec">
                      <span className="template-spec-label">Skill Level</span>
                      <span className="template-spec-value">{template.skillLevel}</span>
                    </div>
                  )}
                  
                  {template.requiresConsultation && (
                    <div className="template-spec template-spec-tag">
                      <span>Requires Consultation</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="template-card-footer">
                <button
                  className="tesla-button-primary"
                  onClick={() => handleUseTemplate(template)}
                >
                  Use Template
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 5V19H5V5H19ZM21 3H3V21H21V3ZM13 7H11V9H9V11H11V13H13V11H15V9H13V7Z" fill="currentColor"/>
              </svg>
            </div>
            <h3>No Templates Available</h3>
            <p>No templates found for the selected category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceTemplatePage;